/**
 * LibraryEmptyState — friendly placeholder for new users (no history yet).
 *
 * Per doc 06 §6 — 3 variants handled by the `variant` prop:
 *  - 'no_data'    : generic 0-history state ("Aisyah baru mulai…")
 *  - 'new_user'   : alias for no_data (same copy, different intent)
 *  - 'error'      : API failure state with retry button
 *
 * The screen-level component decides which to render based on the
 * fetched data + loading state.
 */

import { Pressable, StyleSheet, Text, View } from 'react-native';

import { AppIcon } from '@/components/app-icon';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { MISC_ICON } from '@/lib/icons';

interface LibraryEmptyStateProps {
  variant: 'no_data' | 'new_user' | 'filtered' | 'error';
  childName?: string;
  errorMessage?: string;
  onRetry?: () => void;
}

export function LibraryEmptyState({
  variant,
  childName,
  errorMessage,
  onRetry,
}: LibraryEmptyStateProps) {
  const config = getConfig(variant, childName);

  return (
    <View style={styles.container}>
      {variant === 'error' ? null : (
        <AppIcon source={MISC_ICON.books} size={72} />
      )}
      <Text style={styles.title}>{config.title}</Text>
      <Text style={styles.body}>{config.body}</Text>
      {variant === 'error' && onRetry ? (
        <Pressable onPress={onRetry} style={styles.retryBtn}>
          <Text style={styles.retryText}>Coba lagi</Text>
        </Pressable>
      ) : null}
      {errorMessage ? (
        <Text style={styles.errorDetail}>{errorMessage}</Text>
      ) : null}
    </View>
  );
}

function getConfig(
  variant: 'no_data' | 'new_user' | 'filtered' | 'error',
  childName?: string,
) {
  switch (variant) {
    case 'no_data':
    case 'new_user':
      return {
        title: childName ? `${childName} baru mulai` : 'Belum ada worksheet',
        body:
          'Insight dan daftar worksheet akan muncul di sini setelah kamu menyetujui worksheet pertama.',
      };
    case 'filtered':
      return {
        title: 'Belum ada worksheet di rentang ini',
        body: 'Coba pilih filter Semua waktu untuk melihat seluruh worksheet yang pernah dipilih.',
      };
    case 'error':
      return {
        title: 'Gagal memuat aktivitas',
        body: 'Coba muat ulang halaman ini.',
      };
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.four,
    gap: Spacing.three,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: Brand.primary,
    textAlign: 'center',
  },
  body: {
    fontSize: 14,
    color: '#6b6573',
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 320,
  },
  retryBtn: {
    marginTop: Spacing.three,
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.two,
    backgroundColor: Brand.primary,
    borderRadius: Radii.medium,
  },
  retryText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  errorDetail: {
    fontSize: 12,
    color: '#dc2626',
    textAlign: 'center',
    fontFamily: 'monospace',
    maxWidth: 320,
  },
});
